import type { PortalClientDto } from '@supply-mover-2-0/shared';

/**
 * The model behind the storefront history filters (HistoryFilters in
 * components/HistoryFilterBar.tsx): the date-range presets and how they
 * resolve, the filter field shapes, what counts as an active filter, and the
 * active-filter chips (PD-17321). Kept free of React so it can be tested on
 * its own; the component re-exports what the pages import.
 */

/** How many previous whole years to offer beneath the current one. */
const PREVIOUS_YEARS = 4;

export const DEFAULT_HISTORY_RANGE = '3m';

export interface HistoryRangeBounds {
	/** Inclusive ISO date lower bound, when the range has one. */
	dateFrom?: string;
	/** Inclusive ISO date upper bound, when the range has one. */
	dateTo?: string;
}

function isoDate(d: Date): string {
	const y = d.getFullYear();
	const m = `${d.getMonth() + 1}`.padStart(2, '0');
	const day = `${d.getDate()}`.padStart(2, '0');
	return `${y}-${m}-${day}`;
}

/** The selectable range keys, in display order. */
export function historyRangeOptions(): Array<{ key: string; label: string }> {
	const now = new Date();
	const years: Array<{ key: string; label: string }> = [];
	for (
		let y = now.getFullYear();
		y >= now.getFullYear() - PREVIOUS_YEARS;
		y--
	) {
		years.push({ key: `${y}`, label: `${y}` });
	}
	return [
		{ key: '30d', label: 'Last 30 days' },
		{ key: '3m', label: 'Last 3 months' },
		...years,
		// Explicit full-history escape hatch (PD-14699) — also the target of
		// the filtered-empty state's "View all time" action.
		{ key: 'all', label: 'All time' },
		// Explicit from/to bounds (PD-14899) — selecting this reveals the
		// From/To date inputs in the tray.
		{ key: 'custom', label: 'Custom range' },
	];
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Push an inclusive ISO DATE upper bound to the end of its day. The API
 * compares `<=` verbatim, so a bare `YYYY-MM-DD` bound excludes everything
 * after midnight on DATETIME columns (transactions); `T23:59:59` covers the
 * whole day there while still excluding the NEXT day on DATE columns
 * (statements' asOfDate — where a day-after bound would wrongly include
 * statements issued exactly on it, e.g. monthly statements on the 1st).
 */
function endOfDayBound(date: string): string {
	return `${date}T23:59:59`;
}

/**
 * Resolve a range key to concrete inclusive ISO date bounds for the API.
 * 'all' means no bounds (an explicit user choice); 'custom' uses the given
 * from/to bounds (PD-14899); unknown keys — or a custom range with no
 * usable bound — fall back to the default (last 3 months) so a hand-edited
 * URL can't silently show the unfiltered full history.
 */
export function resolveHistoryRange(
	range: string,
	customFrom?: string,
	customTo?: string,
): HistoryRangeBounds {
	const now = new Date();
	if (range === 'all') {
		return {};
	}
	if (range === 'custom') {
		const from =
			customFrom != null && ISO_DATE_RE.test(customFrom)
				? customFrom
				: undefined;
		const to =
			customTo != null && ISO_DATE_RE.test(customTo) ? customTo : undefined;
		if (from != null || to != null) {
			return {
				dateFrom: from,
				dateTo: to != null ? endOfDayBound(to) : undefined,
			};
		}
		// No usable bound → fall through to the default below.
	}
	if (/^\d{4}$/.test(range)) {
		const year = Number(range);
		return {
			dateFrom: `${year}-01-01`,
			dateTo: endOfDayBound(`${year}-12-31`),
		};
	}
	if (range === '30d') {
		const from = new Date(now);
		from.setDate(from.getDate() - 30);
		return { dateFrom: isoDate(from) };
	}
	// '3m' and anything unrecognized → last 3 months.
	const from = new Date(now);
	from.setMonth(from.getMonth() - 3);
	return { dateFrom: isoDate(from) };
}

/**
 * Map the tray's committed draft to the range/from/to URL-param updates
 * (PD-14899) — shared by every history surface so the custom-bounds rules
 * stay identical: `from`/`to` persist only for an applied custom range, and
 * a custom pick with both bounds blank is not a filter at all.
 */
export function historyRangeParamUpdates(
	next: Record<string, string | undefined>,
	defaultRange: string = DEFAULT_HISTORY_RANGE,
): Record<string, string | null> {
	const range = next.range ?? '';
	const from = next.from ?? '';
	const to = next.to ?? '';
	const hasCustomBounds = range === 'custom' && (from !== '' || to !== '');
	return {
		range:
			range.length > 0 &&
			range !== defaultRange &&
			(range !== 'custom' || hasCustomBounds)
				? range
				: null,
		from: hasCustomBounds && from !== '' ? from : null,
		to: hasCustomBounds && to !== '' ? to : null,
	};
}

export function historyRangeLabel(range: string): string {
	const match = historyRangeOptions().find((o) => o.key === range);
	return match?.label ?? 'Last 3 months';
}

// --- Filter field configuration ---

/** A dropdown filter (document type, payment status). */
interface SelectFilterField {
	kind: 'select';
	/** URL/param key this field maps to. */
	key: string;
	label: string;
	/** Options; include an empty-value option for "All". */
	options: Array<{ value: string; label: string }>;
}

/** The relative date-range presets (shared across every surface). */
interface RangeFilterField {
	kind: 'range';
	/** Always 'range'. */
	key: 'range';
	label: string;
	/**
	 * The range key an empty value stands for — shown in the select and
	 * treated as "not an active filter". History surfaces keep the '3m'
	 * default; the Make-a-Payment open-invoice list uses 'all' (hiding an
	 * old unpaid invoice by default would hide exactly what most needs
	 * paying).
	 */
	defaultRange?: string;
}

/** The account/location picker — only when the visitor has multiple. */
interface LocationFilterField {
	kind: 'location';
	key: 'clientId';
	label: string;
	clients: Array<PortalClientDto>;
}

/** A yes/no checkbox filter (e.g. "Overdue only"). '1' = on, '' = off. */
interface ToggleFilterField {
	kind: 'toggle';
	key: string;
	label: string;
}

export type HistoryFilterField =
	| SelectFilterField
	| RangeFilterField
	| LocationFilterField
	| ToggleFilterField;

/** Is a committed value for a field an active (non-default) filter? */
function isFieldActive(field: HistoryFilterField, value: string): boolean {
	if (field.kind === 'range') {
		// '' is the field's default range; anything else is an explicit pick.
		return (
			value !== '' && value !== (field.defaultRange ?? DEFAULT_HISTORY_RANGE)
		);
	}
	return value !== '';
}

/**
 * The number on the Filters button. With `countDefaultRange` (PD-17321, the
 * surfaces that show the active-filter chips) the date range always counts,
 * default included, so the badge agrees with the chips: the date chip is
 * always there because a history list is always limited to some range.
 */
export function countActiveFilters(
	fields: Array<HistoryFilterField>,
	values: Record<string, string>,
	countDefaultRange = false,
): number {
	return fields.reduce(
		(n, f) =>
			n +
			((countDefaultRange && f.kind === 'range') ||
			isFieldActive(f, values[f.key] ?? '')
				? 1
				: 0),
		0,
	);
}

// --- Active-filter chips (PD-17321) ---

/** The search chip's id. Removing it commits `q: ''` through onApply. */
export const HISTORY_SEARCH_CHIP_ID = 'q';

/** The range a committed `range` / `from` / `to` actually applies. */
export type EffectiveHistoryRangeModel =
	| { kind: 'all' }
	| { kind: 'custom'; from: string | null; to: string | null }
	| { kind: 'preset'; key: string };

/**
 * Which range is really in effect, with the same fallbacks as
 * resolveHistoryRange: a blank key is the field's default, and a custom range
 * with no usable bound or an unrecognized key is the last 3 months. The chip
 * has to name what the list is actually showing, not what the URL says.
 */
export function effectiveHistoryRange(
	range: string,
	from: string,
	to: string,
	defaultRange: string = DEFAULT_HISTORY_RANGE,
): EffectiveHistoryRangeModel {
	const key = range === '' ? defaultRange : range;
	if (key === 'all') {
		return { kind: 'all' };
	}
	if (key === 'custom') {
		const fromBound = ISO_DATE_RE.test(from) ? from : null;
		const toBound = ISO_DATE_RE.test(to) ? to : null;
		if (fromBound != null || toBound != null) {
			return { kind: 'custom', from: fromBound, to: toBound };
		}
		return { kind: 'preset', key: DEFAULT_HISTORY_RANGE };
	}
	if (key === '30d' || /^\d{4}$/.test(key)) {
		return { kind: 'preset', key };
	}
	return { kind: 'preset', key: DEFAULT_HISTORY_RANGE };
}

export interface HistoryFilterChipModel {
	/** What removing the chip clears: 'range', a field key, or HISTORY_SEARCH_CHIP_ID. */
	id: string;
	/** English name shown before the value ('Date', 'Search', the field's label). */
	label: string;
	/**
	 * English value, with {{placeholders}} filled from `params`; null for a
	 * toggle, which shows its label alone. The caller translates both.
	 */
	value: string | null;
	params?: Record<string, string>;
	/** False only for the date chip while it already shows All time. */
	removable: boolean;
}

interface HistoryFilterChipsInputModel {
	fields: Array<HistoryFilterField>;
	/** Committed values, keyed by field key, as HistoryFilters receives them. */
	values: Record<string, string>;
	/** The committed search (not what is being typed); a chip when not blank. */
	appliedSearch?: string;
	/** Formats an ISO date (YYYY-MM-DD) for the custom-range chip. */
	formatDate: (isoDate: string) => string;
}

function rangeChip(
	field: RangeFilterField,
	values: Record<string, string>,
	formatDate: (isoDate: string) => string,
): HistoryFilterChipModel {
	const effective = effectiveHistoryRange(
		values.range ?? '',
		values.from ?? '',
		values.to ?? '',
		field.defaultRange,
	);
	const chip = { id: 'range', label: 'Date', removable: true };
	if (effective.kind === 'all') {
		// Removing the date chip means "all time", so there is nothing left to
		// remove once it already says so.
		return { ...chip, value: 'All time', removable: false };
	}
	if (effective.kind === 'custom') {
		if (effective.from != null && effective.to != null) {
			return {
				...chip,
				value: '{{from}} – {{to}}',
				params: {
					from: formatDate(effective.from),
					to: formatDate(effective.to),
				},
			};
		}
		return effective.from != null
			? {
					...chip,
					value: 'From {{date}}',
					params: { date: formatDate(effective.from) },
				}
			: {
					...chip,
					value: 'Through {{date}}',
					params: { date: formatDate(effective.to ?? '') },
				};
	}
	// A year older than the picker offers still names itself, rather than
	// falling back to the default's label.
	return {
		...chip,
		value: /^\d{4}$/.test(effective.key)
			? effective.key
			: historyRangeLabel(effective.key),
	};
}

function fieldChip(
	field: Exclude<HistoryFilterField, RangeFilterField>,
	value: string,
): HistoryFilterChipModel | null {
	if (value === '') {
		return null;
	}
	const chip = { id: field.key, label: field.label, removable: true };
	switch (field.kind) {
		case 'location': {
			// The account's name, as the picker shows it; a clientId that is not
			// one of the visitor's accounts (a stale or hand-edited link) still
			// gets a chip, so the narrowing is never invisible.
			const name = field.clients
				.find((client) => String(client.id) === value)
				?.name?.trim();
			return name != null && name !== ''
				? { ...chip, value: name }
				: { ...chip, value: 'Account #{{id}}', params: { id: value } };
		}
		case 'select': {
			const option = field.options.find((o) => o.value === value);
			return { ...chip, value: option?.label ?? value };
		}
		case 'toggle':
			return value === '1' ? { ...chip, value: null } : null;
	}
}

/**
 * The chips for what is applied: the date range first (always, when the
 * surface has one), then the search, then the other filters in tray order.
 */
export function historyFilterChips({
	fields,
	values,
	appliedSearch,
	formatDate,
}: HistoryFilterChipsInputModel): Array<HistoryFilterChipModel> {
	const chips: Array<HistoryFilterChipModel> = [];
	const range = fields.find(
		(field): field is RangeFilterField => field.kind === 'range',
	);
	if (range != null) {
		chips.push(rangeChip(range, values, formatDate));
	}
	const search = appliedSearch?.trim() ?? '';
	if (search !== '') {
		chips.push({
			id: HISTORY_SEARCH_CHIP_ID,
			label: 'Search',
			value: search,
			removable: true,
		});
	}
	for (const field of fields) {
		if (field.kind === 'range') {
			continue;
		}
		const chip = fieldChip(field, values[field.key] ?? '');
		if (chip != null) {
			chips.push(chip);
		}
	}
	return chips;
}

/** Every field key present, blank unless committed, plus the custom bounds. */
function completeValues(
	fields: Array<HistoryFilterField>,
	values: Record<string, string>,
): Record<string, string> {
	const complete: Record<string, string> = { from: '', to: '' };
	for (const field of fields) {
		complete[field.key] = '';
	}
	return { ...complete, ...values };
}

/**
 * The onApply payload that removes one chip and keeps everything else. The
 * date chip goes to All time; the search chip adds `q: ''`, which a page with a
 * search box maps to its search param. Every field key is present, as the
 * pages' handlers expect from the tray.
 */
export function historyValuesWithoutChip(
	chipId: string,
	fields: Array<HistoryFilterField>,
	values: Record<string, string>,
): Record<string, string> {
	const next = completeValues(fields, values);
	if (chipId === 'range') {
		return { ...next, range: 'all', from: '', to: '' };
	}
	return { ...next, [chipId]: '' };
}

/**
 * Every filter back to its default: each field key and each committed key
 * blank. The committed keys matter because a page reads keys its tray does not
 * show (Orders reads `clientId` even when a one-account visitor has no
 * Location field), and a payload without them threw in the page's handler.
 */
export function blankHistoryValues(
	fields: Array<HistoryFilterField>,
	values: Record<string, string>,
): Record<string, string> {
	const blank = completeValues(fields, {});
	for (const key of Object.keys(values)) {
		blank[key] = '';
	}
	return blank;
}

/** The onApply payload for Clear all: every filter at its default and no search. */
export function clearedHistoryValues(
	fields: Array<HistoryFilterField>,
	values: Record<string, string>,
): Record<string, string> {
	return { ...blankHistoryValues(fields, values), [HISTORY_SEARCH_CHIP_ID]: '' };
}

/** Whether Clear all would change anything: a search, or any non-default filter. */
export function historyFiltersDifferFromDefaults(
	fields: Array<HistoryFilterField>,
	values: Record<string, string>,
	appliedSearch?: string,
): boolean {
	return (
		(appliedSearch?.trim() ?? '') !== '' ||
		countActiveFilters(fields, values) > 0
	);
}
